
import { defineComponent, onMounted, ref, PropType, onUnmounted, reactive } from "vue";
import AppLayout from "@/Layouts/AppLayout.vue";
import Modal from "@/Components/General/Modal.vue";
import Multiselect from "@/Components/General/Multiselect.vue";
import Card from "@/Components/General/Card.vue";
import onScan from "onscan.js";
import Button from "@/Components/Buttons/Button.vue";
import { Inertia } from "@inertiajs/inertia";
import type { ProductRemember } from "@/types/ProductRemember";
import route from "ziggy-js";
import { mdiLoading, mdiPlus, mdiMinus, mdiClose, mdiFileExport } from "@mdi/js";
import SvgIcon from "@jamescoyle/vue-icon";
import Messages from "@/Components/General/Messages.vue";
import { debounce } from "lodash";
import ProductSelect from "@/Components/Product/ProductSelect.vue";
import { useForm } from "@inertiajs/inertia-vue3";
import ValidationFormErrors from "@/Components/Forms/ValidationFormErrors.vue";
import TextButton from "@/Components/Buttons/TextButton.vue";
import Product from "@/types/Product";

export default defineComponent({
  layout: AppLayout,
  props: {
    remembers: {
      type: Object as PropType<ProductRemember[]>,
    },
    admin: {
      type: Boolean,
    },
    developer: {
      type: Boolean,
    },
  },
  setup(props) {
    const barcodeSim = ref("");
    const barcodeSimOpen = ref(false);
    const activeTab = ref("datovarer");
    const currentProduct = ref<Product | null>(null);
    const expiryDateErrors = reactive<{ [id: number]: string }>({});

    const switchTab = (tab: string) => {
      activeTab.value = tab;
      sortProducts();
    };

    const form = useForm({
      product_id: null,
      amount: 1,
      updateAmount: false,
      expiryDate: null,
      updateExpiryDate: false,
      type: "datovarer",
    });

    const addProduct = () => {
      form.type = activeTab.value;
      form.updateAmount = true;
      form.updateExpiryDate = true;

      form.post(route("products.remember-list.store"), {
        preserveScroll: true,
        onSuccess: () => {
          form.product_id = null;
          form.amount = 1;
          form.updateAmount = false;
          form.expiryDate = null;
          sortProducts();
        },
      });
    };

    const inputAmount = (id: number, amount: number | undefined, event: Event) => {
      const newAmount = Number((event.target as HTMLInputElement).value);

      if (isNaN(newAmount) && !Number.isInteger(newAmount)) {
        return;
      }

      if (amount === newAmount) {
        return;
      }

      Inertia.patch(route("products.remember-list.update", id), {
        amount: newAmount,
        inputAmount: true,
      }, {
        preserveScroll: true,
        onSuccess: () => {
          sortProducts();
        },
      });
    };

    const changeAmount = (id: number, changeByAmount: number) => {
      Inertia.patch(route("products.remember-list.update", id), {
        amount: changeByAmount,
        updateAmount: true,
      }, {
        preserveScroll: true,
        onSuccess: () => {
          sortProducts();
        }
      });
    };

    const updateExpiryDate = (id: number, expiryDate: string | undefined) => {
      Inertia.patch(route('products.remember-list.update', id), {
        expiryDate: expiryDate ?? null,
        updateExpiryDate: true,
        type: activeTab.value
      }, {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          expiryDateErrors[id] = '';
          sortProducts();
        },
        onError: (errors) => {
          console.log(errors);
          expiryDateErrors[id] = errors.expiryDate || 'Ugyldig utløpsdato';
        }
      });
    };

    const updateComment = (id: number, comment: string | undefined, event: Event) => {
      Inertia.patch(route('products.remember-list.update', id), {
        comment: comment || null,
        updateComment: true,
      }, {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => { },
      });
    };

    const simScan = () => {
      onScan.simulate(document, barcodeSim.value);
    };

    const removeProductLoading = ref(false);

    const removeProduct = (id: number) => {
      removeProductLoading.value = true;

      Inertia.delete(route('products.remember-list.destroy', id), {
        preserveScroll: true,
        preserveState: true,
        onFinish: () => {
          removeProductLoading.value = false;
          sortProducts();
        },
      });
    };

    const moveProductLoading = ref(false);

    const moveProduct = (id: number) => {
      moveProductLoading.value = true;

      Inertia.patch(route('products.remember-list.update', id), {
        type: activeTab.value === "datovarer" ? "datovareuttaksliste" : "datovarer",
      }, {
        preserveScroll: true,
        preserveState: true,
        onFinish: () => {
          moveProductLoading.value = false;
          sortProducts();
        },
      });
    };

    const windowsSize = ref("sm");

    const checkSize = (event?: Event) => {
      windowsSize.value = "sm";
      console.log(window.innerWidth);

      if (window.innerWidth > 768) {
        windowsSize.value = "md";
      }

      if (window.innerWidth > 1080) {
        windowsSize.value = "lg";
      }
    };

    window.addEventListener("resize", debounce(checkSize, 250));

    checkSize();

    onMounted(() => {
      onScan.attachTo(document, {
        onScan: async (sCode: string, iQty: number) => {
          Inertia.post(route("products.remember-list.store"), {
            barcode: sCode,
            type: activeTab.value,
          }, {
            preserveScroll: true,
            onSuccess: () => {
              sortProducts();
            },
          });
        },
        minLength: 4,
      });

      (window as any).onScan = onScan;

      sortProducts();
    });

    onUnmounted(() => {
      onScan.detachFrom(document);
    });

    const emptyListOpen = ref(false);
    const emptyListLoading = ref(false);

    const emptyList = () => {
      emptyListLoading.value = true;
      Inertia.delete(route("products.remember-list.empty", { type: activeTab.value }), {
        preserveScroll: true,

        onSuccess: () => {
          emptyListOpen.value = false;
          sortProducts();
        },
        onFinish: () => {
          emptyListLoading.value = false;
        },
      });
    };

    const showProductImageModal = ref(false);

    const showImage = (remember: ProductRemember) => {
      currentProduct.value = remember.product;
      showProductImageModal.value = true;
    };

    const exportRememberList = () => {
      let type = activeTab.value;
      let params = new URLSearchParams();

      params.append('type', type);

      const query = params.toString();

      window.location.href = `${route('products.remember-list.export')}?${query}`;
    };

    const sortBy = ref<'product_id' | 'amount' | 'expiry_date'>('expiry_date');
    const sortDirection = ref<'asc' | 'desc'>('asc');

    const sortedRemembers = ref<ProductRemember[]>([]);
    const rememberProducts = (): ProductRemember[] => {
      return sortedRemembers.value;
    }

    const sortProducts = () => {
      const products = props.remembers?.filter(item => item.type === activeTab.value) || [];
      const direction = sortDirection.value === 'asc' ? 1 : -1;

      const getValue = (item: ProductRemember): any => {
        switch (sortBy.value) {
          case 'product_id':
            return item.product_id;
          case 'amount':
            return item.amount ?? 0;
          case 'expiry_date':
            return item.expiry_date ?? '';
        }
      };

      sortedRemembers.value = products.sort((a, b) => {
        const aValue = getValue(a);
        const bValue = getValue(b);

        if (aValue < bValue) return -1 * direction;
        if (aValue > bValue) return 1 * direction;
        return 0;
      });
    };

    return {
      form,
      route,
      simScan,
      emptyList,
      addProduct,
      barcodeSim,
      mdiPlus,
      mdiMinus,
      mdiLoading,
      windowsSize,
      removeProduct,
      changeAmount,
      emptyListOpen,
      barcodeSimOpen,
      emptyListLoading,
      removeProductLoading,
      updateComment,
      switchTab,
      activeTab,
      showImage,
      showProductImageModal,
      closeIcon: mdiClose,
      inputAmount,
      rememberProducts,
      currentProduct,
      exportRememberList,
      updateExpiryDate,
      mdiFileExport,
      sortBy,
      sortDirection,
      sortProducts,
      expiryDateErrors,
      moveProduct,
      moveProductLoading,
    };
  },
  components: {
    Modal,
    Button,
    SvgIcon,
    Messages,
    ProductSelect,
    ValidationFormErrors,
    TextButton,
    Multiselect,
    Card,
  },
});
